import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";

type HashInput = string | ArrayBuffer | ArrayBufferView;

const normalizeAlgorithm = (algorithm: string): string =>
  algorithm.toLowerCase().replace(/[^a-z0-9]/g, "");

const inputToBytes = (input: HashInput): Uint8Array => {
  if (typeof input === "string") {
    return utf8ToBytes(input);
  }

  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }

  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(
      input.buffer as ArrayBuffer,
      input.byteOffset,
      input.byteLength
    );
  }

  throw new TypeError("createHash.update() only supports strings and byte arrays.");
};

class BrowserSha256Hash {
  private chunks: Uint8Array[] = [];

  update(input: HashInput): this {
    this.chunks.push(inputToBytes(input));

    return this;
  }

  digest(): Uint8Array;
  digest(encoding: "hex"): string;
  digest(encoding?: "hex"): string | Uint8Array {
    const byteLength = this.chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const bytes = new Uint8Array(byteLength);
    let offset = 0;

    for (const chunk of this.chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }

    const hashed = sha256(bytes);

    if (encoding === undefined) {
      return hashed;
    }

    if (encoding === "hex") {
      return bytesToHex(hashed);
    }

    throw new Error(`Unsupported digest encoding: ${String(encoding)}`);
  }
}

export const createHash = (algorithm: string): BrowserSha256Hash => {
  if (normalizeAlgorithm(algorithm) !== "sha256") {
    throw new Error(`Unsupported browser hash algorithm: ${algorithm}`);
  }

  return new BrowserSha256Hash();
};

export default {
  createHash
};
